import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ notification, onDismiss }) => {
  if (!notification) return null;
  const isSuccess = notification.type === 'success';
  const isWarn    = notification.type === 'warn';
  const bg        = isSuccess ? 'bg-emerald-500' : isWarn ? 'bg-amber-500' : 'bg-red-500';
  const iconName  = isSuccess ? 'CheckCircle' : isWarn ? 'AlertTriangle' : 'AlertCircle';
  return (
    <div className={`fixed top-8 left-1/2 -translate-x-1/2 z-[200] px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 ${bg} text-white max-w-[92vw]`}>
      <Icon name={iconName} size={18} className="shrink-0" />
      <span className="text-sm font-black uppercase tracking-wider">{notification.message}</span>
      <button onClick={onDismiss} className="ml-2 shrink-0 w-5 h-5 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-all">
        <Icon name="X" size={11} />
      </button>
    </div>
  );
};


// ─── Confirm Dialog ───────────────────────────────────────────────────────────
const ConfirmDialog = ({ open, message, sublabel, onConfirm, onCancel, confirmLabel = 'Conferma', confirmColor = 'bg-red-500 hover:bg-red-600 text-white border-red-600' }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={onCancel} />
      <div className="relative w-full max-w-sm bg-card border border-border/60 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/50 to-transparent" />
        <div className="p-7 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-base font-black uppercase tracking-tight text-foreground">{message}</span>
            {sublabel && <span className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest">{sublabel}</span>}
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-2xl bg-muted hover:bg-muted/80 border border-border/40 text-foreground text-[11px] font-black uppercase tracking-widest transition-all">Annulla</button>
            <button onClick={onConfirm} className={`flex-1 px-4 py-2.5 rounded-2xl border text-[11px] font-black uppercase tracking-widest transition-all ${confirmColor}`}>{confirmLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ─── SectionTitle ─────────────────────────────────────────────────────────────
const SectionTitle = ({ label, icon }) => (
  <div className="flex items-center gap-3 col-span-full mt-3">
    {icon && <Icon name={icon} size={13} className="text-primary/60" />}
    <span className="text-[10px] font-black uppercase tracking-[.25em] text-primary/70">{label}</span>
    <div className="flex-1 h-px bg-border/30" />
  </div>
);


// ─── Campo con datalist (free-text + autocomplete) ────────────────────────────
const ComboField = ({ label, name, value, onChange, options = [], placeholder, required, error, disabled, icon, sublabel }) => (
  <div className="flex flex-col gap-1.5 w-full group">
    <div className="flex justify-between items-center px-1">
      <label className={`text-[10px] font-black uppercase tracking-[.15em] transition-colors select-none ${
        error ? 'text-red-500' : disabled ? 'text-muted-foreground/30' : 'text-muted-foreground/60 group-focus-within:text-primary'
      }`}>
        {label}{required && <span className="text-primary ml-0.5">•</span>}
        {sublabel && <span className="ml-1.5 text-muted-foreground/30 normal-case font-bold text-[9px] tracking-normal">{sublabel}</span>}
      </label>
      {error && (
        <span className="text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1">
          <Icon name="AlertCircle" size={9} />{error}
        </span>
      )}
    </div>
    <div className="relative">
      {options.length > 0 && (
        <datalist id={`combo-${name}`}>
          {options.map(o => <option key={o} value={o} />)}
        </datalist>
      )}
      {icon && <Icon name={icon} size={13} className={`absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none ${disabled ? 'text-muted-foreground/20' : 'text-primary/40'}`} />}
      <input
        type="text"
        name={name}
        value={value ?? ''}
        onChange={onChange}
        list={options.length > 0 ? `combo-${name}` : undefined}
        placeholder={disabled ? '— bloccato —' : placeholder}
        disabled={disabled}
        className={`
          w-full rounded-2xl py-3 text-sm font-bold outline-none transition-all
          ${icon ? 'pl-9 pr-4' : 'px-4'}
          ${disabled
            ? 'opacity-40 cursor-not-allowed bg-muted/10 border border-border/20 text-muted-foreground'
            : error
              ? 'bg-red-500/5 border-2 border-red-500/40 focus:ring-2 focus:ring-red-500/10'
              : 'bg-muted/30 border border-border/30 focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60'
          }
        `}
      />
    </div>
  </div>
);


// ─── Campo datetime-local ─────────────────────────────────────────────────────
const DatetimeField = ({ label, name, value, onChange, required, error, type = 'datetime-local' }) => (
  <div className="flex flex-col gap-1.5 w-full group">
    <div className="flex justify-between items-center px-1">
      <label className={`text-[10px] font-black uppercase tracking-[.15em] select-none ${error ? 'text-red-500' : 'text-muted-foreground/60 group-focus-within:text-primary'}`}>
        {label}{required && <span className="text-primary ml-0.5">•</span>}
      </label>
      {error && <span className="text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1"><Icon name="AlertCircle" size={9} />{error}</span>}
    </div>
    <input
      type={type}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      className={`w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all ${
        error
          ? 'bg-red-500/5 border-2 border-red-500/40 focus:ring-2 focus:ring-red-500/10'
          : 'bg-muted/30 border border-border/30 focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60'
      }`}
    />
  </div>
);


// ─── Badge info cilindar ─────────────────────────────────────────────────────
const CilindroBadge = ({ ncilindro, stato, lastLinea, lastColata }) => {
  if (!ncilindro) return null;
  return (
    <div className="col-span-full p-4 rounded-2xl bg-primary/5 border border-primary/20 flex flex-wrap gap-4 items-center">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
        <span className="text-[10px] font-black uppercase tracking-widest text-primary/70">Cilindro selezionato</span>
      </div>
      <div className="flex flex-wrap gap-3">
        {[
          { label: 'N°', val: ncilindro },
          { label: 'Stato att.', val: stato || 'N/D' },
          lastLinea   && { label: 'Ultima linea',   val: lastLinea   },
          lastColata  && { label: 'Ultima colata',  val: lastColata  },
        ].filter(Boolean).map(({ label, val }) => (
          <span key={label} className="flex items-center gap-1.5 text-[10px] font-bold text-foreground/60">
            <span className="text-muted-foreground/40 uppercase tracking-widest text-[9px]">{label}</span>
            <span className="font-black text-foreground/80">{val}</span>
          </span>
        ))}
      </div>
    </div>
  );
};


// ─── Componente principale ────────────────────────────────────────────────────
const NewInsertCilindri = ({ isOpen, onClose, onRefresh }) => {

  const emptyForm = {
    dataintervento: '', tipointervento: '', ncilindro: '',
    colata: '', linea: '', carropaniera: '',
    stato: '', datarientro: '', note: '',
  };

  const [formData, setFormData]         = useState(emptyForm);
  const [errors, setErrors]             = useState({});
  const [isLoading, setIsLoading]       = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);

  // profilo utente loggato
  const [currentUser, setCurrentUser] = useState(null);

  const [opts, setOpts] = useState({
    tipointervento: [], ncilindro: [], colata: [], linea: [], carropaniera: [], stato: [],
  });

  const [cilindrInfo, setCilindrInfo] = useState(null);

  const tipo = formData.tipointervento?.toUpperCase();
  const isSmontato  = tipo === 'SMONTATO';
  const isMontato   = tipo === 'MONTATO';
  const isRientrato = tipo === 'RIENTRATO';
  const isAltro     = tipo === 'ALTRO';

  // ── Fetch profilo utente loggato ──────────────────────────────────────────
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: profilo } = await supabase
          .from('profili')
          .select('nome, cognome')
          .eq('id', user.id)
          .single();
        if (profilo) {
          setCurrentUser({ id: user.id, nome: profilo.nome || '', cognome: profilo.cognome || '' });
        }
      } catch (err) {
        console.error('Fetch current user error:', err);
      }
    };
    fetchCurrentUser();
  }, []);

  // ── Auto-dismiss toast success ────────────────────────────────────────────
  useEffect(() => {
    if (!notification || notification.type !== 'success') return;
    const t = setTimeout(() => setNotification(null), 5000);
    return () => clearTimeout(t);
  }, [notification]);

  // ── Blocca scroll body ────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      fetchBaseOptions();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // ── Escape ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, formData]);

  // ── Fetch opzioni base ────────────────────────────────────────────────────
  const fetchBaseOptions = async () => {
    try {
      const fields = ['tipointervento', 'colata', 'linea', 'carropaniera'];
      const newOpts = { ...opts };
      for (const f of fields) {
        const { data } = await supabase.from('cilindripaniera').select(f).not(f, 'is', null);
        newOpts[f] = [...new Set(data.map(r => r[f]).filter(Boolean))].sort();
      }
      setOpts(newOpts);
    } catch (err) {
      console.error('Fetch opts error:', err);
    }
  };

  // ── Fetch ncilindro per MONTATO ───────────────────────────────────────────
  // Cerca GLOBALMENTE tutti i cilindri il cui ultimo record ha stato = 'PRONTO'.
  // Non filtra per posizione — la posizione (colata/linea/carro) è dove vengono montati.
  const fetchCilindriPronte = async () => {
    try {
      const { data } = await supabase
        .from('cilindripaniera')
        .select('ncilindro, stato, dataintervento')
        .not('ncilindro', 'is', null)
        .order('dataintervento', { ascending: false });

      if (!data) return;

      // Per ogni ncilindro prendi solo il record più recente
      const ultimi = {};
      for (const r of data) {
        if (!ultimi[r.ncilindro]) ultimi[r.ncilindro] = r;
      }

      // Tieni solo quelli il cui ultimo stato è PRONTO
      const pronte = Object.values(ultimi)
        .filter(r => r.stato?.toUpperCase() === 'PRONTO')
        .map(r => r.ncilindro)
        .sort();

      setOpts(prev => ({ ...prev, ncilindro: pronte }));
    } catch (err) {
      console.error('Fetch cilindri pronte:', err);
    }
  };

  // ── Fetch ncilindro per RIENTRATO (solo stato IN RIPARAZIONE) ─────────────
  const fetchCilindriRiparazione = async () => {
    try {
      const { data } = await supabase
        .from('cilindripaniera')
        .select('ncilindro, stato, dataintervento')
        .not('ncilindro', 'is', null)
        .order('dataintervento', { ascending: false });

      if (!data) return;

      const ultimi = {};
      for (const r of data) {
        if (!ultimi[r.ncilindro]) ultimi[r.ncilindro] = r;
      }
      const inRip = Object.values(ultimi)
        .filter(r => r.stato?.toUpperCase() === 'IN RIPARAZIONE')
        .map(r => r.ncilindro)
        .sort();

      setOpts(prev => ({ ...prev, ncilindro: inRip }));
    } catch (err) {
      console.error('Fetch cilindri riparazione:', err);
    }
  };

  // ── Fetch ncilindro per SMONTATO ──────────────────────────────────────────
  // Filtra per carropaniera + colata + linea, prende ultimo record per ncilindro
  // e mostra solo quelli il cui ultimo tipointervento era MONTATO
  const fetchCilindriSmontato = async (carropaniera, colata, linea) => {
    if (!carropaniera || !colata || !linea) return;
    try {
      const { data } = await supabase
        .from('cilindripaniera')
        .select('ncilindro, tipointervento, dataintervento, stato, linea, colata')
        .eq('carropaniera', carropaniera)
        .eq('colata', colata)
        .eq('linea', linea)
        .not('ncilindro', 'is', null)
        .order('dataintervento', { ascending: false });

      if (!data) return;

      const ultimi = {};
      for (const r of data) {
        if (!ultimi[r.ncilindro]) ultimi[r.ncilindro] = r;
      }

      const montati = Object.values(ultimi)
        .filter(r => r.tipointervento?.toUpperCase() === 'MONTATO')
        .map(r => r.ncilindro)
        .sort();

      setOpts(prev => ({ ...prev, ncilindro: montati }));
    } catch (err) {
      console.error('Fetch cilindri smontato:', err);
    }
  };

  // ── Fetch info su un cilindro specifico (per badge) ───────────────────────
  const fetchCilindrInfo = async (ncilindro) => {
    if (!ncilindro) { setCilindrInfo(null); return; }
    try {
      const { data } = await supabase
        .from('cilindripaniera')
        .select('ncilindro, stato, linea, colata, carropaniera, dataintervento')
        .eq('ncilindro', ncilindro)
        .order('dataintervento', { ascending: false })
        .limit(1);

      if (data && data[0]) setCilindrInfo(data[0]);
    } catch (err) {
      console.error('Fetch cilindro info:', err);
    }
  };

  // ── Reagisce al cambio tipointervento ─────────────────────────────────────
  useEffect(() => {
    if (!tipo) return;
    setFormData(prev => ({ ...prev, ncilindro: '', colata: '', linea: '', carropaniera: '', stato: '' }));
    setCilindrInfo(null);
    setOpts(prev => ({ ...prev, ncilindro: [] }));

    // RIENTRATO: carica subito tutti IN RIPARAZIONE (no filtro posizione)
    if (isRientrato) fetchCilindriRiparazione();
    // MONTATO: carica subito tutti PRONTO (no filtro posizione — sceglie dove montarli)
    if (isMontato)   fetchCilindriPronte();
    // SMONTATO: ncilindro si carica dopo selezione colata+linea+carro
  }, [tipo]);

  // ── Reagisce al cambio colata/linea/carropaniera (solo SMONTATO) ──────────
  // Per MONTATO la posizione NON influenza la lista ncilindro
  useEffect(() => {
    if (!isSmontato) return;
    const { carropaniera, colata, linea } = formData;

    setFormData(prev => ({ ...prev, ncilindro: '' }));
    setCilindrInfo(null);
    setOpts(prev => ({ ...prev, ncilindro: [] }));

    if (carropaniera && colata && linea) {
      fetchCilindriSmontato(carropaniera, colata, linea);
    }
  }, [formData.carropaniera, formData.colata, formData.linea, isSmontato]);

  // ── Reagisce al cambio ncilindro (badge) ──────────────────────────────────
  useEffect(() => {
    if (formData.ncilindro) {
      fetchCilindrInfo(formData.ncilindro);
    } else {
      setCilindrInfo(null);
    }
  }, [formData.ncilindro]);

  // ── Input change generico ─────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  // ── Validazione ───────────────────────────────────────────────────────────
  const validateForm = () => {
    const errs = {};
    if (!formData.dataintervento) errs.dataintervento = 'Obbligatorio';
    if (!formData.tipointervento) errs.tipointervento = 'Obbligatorio';
    if (!formData.ncilindro)      errs.ncilindro      = 'Obbligatorio';

    if (isSmontato) {
      if (!formData.colata)       errs.colata       = 'Obbligatorio';
      if (!formData.linea)        errs.linea        = 'Obbligatorio';
      if (!formData.carropaniera) errs.carropaniera = 'Obbligatorio';
      if (!formData.note?.trim()) errs.note = 'Obbligatorio per SMONTATO';
    }

    if (isMontato) {
      if (!formData.colata)       errs.colata       = 'Obbligatorio';
      if (!formData.linea)        errs.linea        = 'Obbligatorio';
      if (!formData.carropaniera) errs.carropaniera = 'Obbligatorio';
      if (!formData.note?.trim()) errs.note = 'Obbligatorio per MONTATO';
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setNotification({ type: 'error', message: 'Compila tutti i campi obbligatori!' });
    }
    return Object.keys(errs).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const utenteStr = currentUser
        ? `${currentUser.nome} ${currentUser.cognome}`.trim()
        : null;

      let statoRecord = formData.stato || null;
      if (isSmontato)  statoRecord = 'IN RIPARAZIONE';
      if (isMontato)   statoRecord = null;
      if (isRientrato) statoRecord = 'PRONTO';

      const payload = {
        dataintervento: new Date(formData.dataintervento).toISOString(),
        tipointervento: formData.tipointervento,
        ncilindro:      formData.ncilindro,
        colata:         formData.colata       || null,
        linea:          formData.linea        || null,
        carropaniera:   formData.carropaniera || null,
        stato:          statoRecord,
        datarientro:    formData.datarientro  || null,
        note:           formData.note?.trim() || null,
        created_at:     new Date().toISOString(),
        utente:         utenteStr,
      };

      // 1. Inserisci il nuovo record
      const { error: insertError } = await supabase
        .from('cilindripaniera')
        .insert([payload]);

      if (insertError) throw insertError;

      // 2. Post-insert: per MONTATO trova il record con stato='PRONTO' di quel
      //    ncilindro e metti stato = NULL (il cilindro non è più in magazzino)
      if (isMontato) {
        const { data: prevPronto, error: prevErr } = await supabase
          .from('cilindripaniera')
          .select('id, stato, dataintervento')
          .eq('ncilindro', formData.ncilindro)
          .eq('stato', 'PRONTO')
          .order('dataintervento', { ascending: false })
          .limit(1);

        if (prevErr) throw prevErr;

        if (prevPronto && prevPronto.length > 0) {
          const { error: updateErr } = await supabase
            .from('cilindripaniera')
            .update({ stato: null })
            .eq('id', prevPronto[0].id);

          if (updateErr) throw updateErr;
        }
      }

      // 3. Per RIENTRATO: trova il record con stato='IN RIPARAZIONE' e metti NULL
      if (isRientrato) {
        const { data: prevRecords, error: prevErr } = await supabase
          .from('cilindripaniera')
          .select('id, stato, dataintervento')
          .eq('ncilindro', formData.ncilindro)
          .eq('stato', 'IN RIPARAZIONE')
          .order('dataintervento', { ascending: false })
          .limit(1);

        if (prevErr) throw prevErr;

        if (prevRecords && prevRecords.length > 0) {
          const { error: updateErr } = await supabase
            .from('cilindripaniera')
            .update({ stato: null })
            .eq('id', prevRecords[0].id);

          if (updateErr) throw updateErr;
        }
      }

      // 4. Per SMONTATO: trova il record MONTATO precedente e metti stato = NULL
      if (isSmontato) {
        const { data: prevMontato } = await supabase
          .from('cilindripaniera')
          .select('id, tipointervento, dataintervento')
          .eq('ncilindro', formData.ncilindro)
          .eq('tipointervento', 'MONTATO')
          .order('dataintervento', { ascending: false })
          .limit(1);

        if (prevMontato && prevMontato.length > 0) {
          await supabase
            .from('cilindripaniera')
            .update({ stato: null })
            .eq('id', prevMontato[0].id);
        }
      }

      setNotification({ type: 'success', message: `Intervento "${formData.tipointervento}" su "${formData.ncilindro}" salvato!` });
      onRefresh?.();
      setFormData(emptyForm);
      setErrors({});
      setCilindrInfo(null);
      setOpts(prev => ({ ...prev, ncilindro: [] }));

    } catch (err) {
      setNotification({ type: 'error', message: 'Errore: ' + err.message });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset e chiudi ────────────────────────────────────────────────────────
  const resetAndClose = () => {
    setFormData(emptyForm);
    setErrors({});
    setNotification(null);
    setCilindrInfo(null);
    onClose();
  };

  const handleClose = () => {
    const isDirty = Object.entries(formData).some(([, v]) => v !== '' && v != null);
    if (isDirty) setConfirmClose(true);
    else resetAndClose();
  };

  if (!isOpen) return null;

  // ── Logica lock/unlock campi ──────────────────────────────────────────────
  // MONTATO: ncilindro è subito disponibile (lista PRONTO caricata al cambio tipo)
  //          posizione (colata/linea/carro) si può inserire liberamente dopo
  // SMONTATO: sequenza colata → linea → carro → ncilindro (locked step by step)
  const lockColata       = (!isSmontato && !isMontato) || !formData.tipointervento;
  const lockLinea        = (!isSmontato && !isMontato) || !formData.colata;
  const lockCarropaniera = (!isSmontato && !isMontato) || !formData.linea;
  // Per MONTATO ncilindro NON è locked dalla posizione — si può scegliere subito
  const lockNcilindro    = !formData.tipointervento ||
                           (isSmontato && (!formData.carropaniera || !formData.colata || !formData.linea));
  const noteObbligatoria = isSmontato || isMontato;

  return (
    <>
      <Toast notification={notification} onDismiss={() => setNotification(null)} />

      <ConfirmDialog
        open={confirmClose}
        message="Scartare i dati inseriti?"
        sublabel="Il nuovo intervento non verrà salvato"
        confirmLabel="Esci senza salvare"
        confirmColor="bg-red-500 hover:bg-red-600 text-white border-red-500"
        onConfirm={() => { setConfirmClose(false); resetAndClose(); }}
        onCancel={() => setConfirmClose(false)}
      />

      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 overflow-hidden">
        <div className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300" onClick={handleClose} />

        <div className="relative w-full max-w-3xl bg-card border border-border/50 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-300">

          {/* Glow */}
          <div className="absolute -top-20 -right-20 w-56 h-56 bg-primary/10 blur-[70px] rounded-full pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-56 h-56 bg-primary/5  blur-[70px] rounded-full pointer-events-none" />

          {/* ── HEADER ── */}
          <div className="shrink-0 px-6 md:px-10 py-5 md:py-7 flex items-center justify-between border-b border-border/40 relative z-10 bg-card/60 backdrop-blur-sm">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-sm shrink-0">
                <Icon name="PlusCircle" size={20} />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-black tracking-tighter uppercase text-foreground">Nuovo Intervento</h2>
                <p className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/50 mt-0.5">
                  Gestione Cilindri Paniera — CCO
                  {currentUser && (
                    <span className="ml-2 text-primary/50">
                      · {currentUser.nome} {currentUser.cognome}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button onClick={handleClose} className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-2xl bg-muted/50 hover:bg-red-500/10 hover:text-red-500 border border-border/40 hover:border-red-500/20 text-muted-foreground transition-all group shrink-0">
              <Icon name="X" size={16} className="group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>

          {/* ── FORM ── */}
          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto px-5 md:px-10 py-6 md:py-8 relative z-10"
            style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.15) transparent' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 md:gap-y-5">

              {/* ── Step 1 ── */}
              <SectionTitle label="Step 1 — Quando e Cosa" icon="Clock" />

              <DatetimeField
                label="Data Intervento"
                name="dataintervento"
                value={formData.dataintervento}
                onChange={handleChange}
                required
                error={errors.dataintervento}
                type="datetime-local"
              />

              <ComboField
                label="Tipo Intervento"
                name="tipointervento"
                value={formData.tipointervento}
                onChange={handleChange}
                options={opts.tipointervento.length ? opts.tipointervento : ['MONTATO', 'SMONTATO', 'RIENTRATO', 'ALTRO']}
                placeholder="MONTATO / SMONTATO / ..."
                required
                error={errors.tipointervento}
                icon="Wrench"
              />

              {/* Guida visiva tipo selezionato */}
              {tipo && (
                <div className={`col-span-full flex items-start sm:items-center gap-3 px-4 py-3 rounded-2xl border text-[11px] font-black uppercase tracking-widest ${
                  isMontato   ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600' :
                  isSmontato  ? 'bg-red-500/10 border-red-500/30 text-red-600' :
                  isRientrato ? 'bg-blue-500/10 border-blue-500/30 text-blue-600' :
                                'bg-muted/30 border-border/30 text-muted-foreground'
                }`}>
                  <Icon name={isMontato ? 'ArrowUpCircle' : isSmontato ? 'ArrowDownCircle' : isRientrato ? 'RotateCcw' : 'Circle'} size={16} className="shrink-0 mt-0.5 sm:mt-0" />
                  <span>
                    {isMontato   && 'Montaggio — scegli cilindro PRONTO, poi indica dove viene montato'}
                    {isSmontato  && 'Smontaggio — seleziona colata → linea → carro/paniera → cilindro'}
                    {isRientrato && 'Rientro — seleziona un cilindro IN RIPARAZIONE → diventerà PRONTO'}
                    {isAltro     && 'Intervento generico — compila liberamente'}
                  </span>
                </div>
              )}

              {/* ── Step 2: Cilindro PRONTO (MONTATO — subito disponibile) ── */}
              {isMontato && (
                <>
                  <SectionTitle label="Step 2 — Cilindro da montare" icon="Hash" />

                  <div className="col-span-full">
                    <ComboField
                      label="N° Cilindro"
                      name="ncilindro"
                      value={formData.ncilindro}
                      onChange={handleChange}
                      options={opts.ncilindro}
                      placeholder="Cilindri PRONTO disponibili..."
                      required
                      error={errors.ncilindro}
                      disabled={lockNcilindro}
                      icon="Hash"
                      sublabel={`(${opts.ncilindro.length} PRONTO disponibili)`}
                    />
                  </div>

                  {cilindrInfo && (
                    <CilindroBadge
                      ncilindro={cilindrInfo.ncilindro}
                      stato={cilindrInfo.stato}
                      lastLinea={cilindrInfo.linea}
                      lastColata={cilindrInfo.colata}
                    />
                  )}

                  <SectionTitle label="Step 3 — Dove viene montato" icon="MapPin" />

                  <ComboField
                    label="Colata"
                    name="colata"
                    value={formData.colata}
                    onChange={handleChange}
                    options={opts.colata}
                    placeholder="Seleziona colata..."
                    required
                    error={errors.colata}
                    disabled={lockColata}
                    icon="Layers"
                  />
                  <ComboField
                    label="Linea"
                    name="linea"
                    value={formData.linea}
                    onChange={handleChange}
                    options={opts.linea}
                    placeholder="Seleziona linea..."
                    required
                    error={errors.linea}
                    disabled={lockLinea}
                    icon="GitBranch"
                    sublabel={lockLinea ? '(sblocca dopo colata)' : ''}
                  />
                  <ComboField
                    label="Carro / Paniera"
                    name="carropaniera"
                    value={formData.carropaniera}
                    onChange={handleChange}
                    options={opts.carropaniera}
                    placeholder="Seleziona carro/paniera..."
                    required
                    error={errors.carropaniera}
                    disabled={lockCarropaniera}
                    icon="Truck"
                    sublabel={lockCarropaniera ? '(sblocca dopo linea)' : ''}
                  />
                </>
              )}

              {/* ── Step 2: Posizione poi Cilindro (SMONTATO) ── */}
              {isSmontato && (
                <>
                  <SectionTitle label="Step 2 — Posizione (sblocca in sequenza)" icon="MapPin" />

                  <ComboField
                    label="Colata"
                    name="colata"
                    value={formData.colata}
                    onChange={handleChange}
                    options={opts.colata}
                    placeholder="Seleziona colata..."
                    required
                    error={errors.colata}
                    disabled={lockColata}
                    icon="Layers"
                  />
                  <ComboField
                    label="Linea"
                    name="linea"
                    value={formData.linea}
                    onChange={handleChange}
                    options={opts.linea}
                    placeholder="Seleziona linea..."
                    required
                    error={errors.linea}
                    disabled={lockLinea}
                    icon="GitBranch"
                    sublabel={lockLinea ? '(sblocca dopo colata)' : ''}
                  />
                  <ComboField
                    label="Carro / Paniera"
                    name="carropaniera"
                    value={formData.carropaniera}
                    onChange={handleChange}
                    options={opts.carropaniera}
                    placeholder="Seleziona carro/paniera..."
                    required
                    error={errors.carropaniera}
                    disabled={lockCarropaniera}
                    icon="Truck"
                    sublabel={lockCarropaniera ? '(sblocca dopo linea)' : ''}
                  />
                </>
              )}

              {/* ── Step 3: Cilindro (SMONTATO / RIENTRATO / ALTRO) ── */}
              {(isSmontato || isRientrato || isAltro) && (
                <>
                  <SectionTitle
                    label={`Step ${isSmontato ? '3' : '2'} — Cilindro`}
                    icon="Cylinder"
                  />

                  <div className="col-span-full">
                    <ComboField
                      label="N° Cilindro"
                      name="ncilindro"
                      value={formData.ncilindro}
                      onChange={handleChange}
                      options={opts.ncilindro}
                      placeholder={
                        isSmontato  ? (lockNcilindro ? 'Seleziona prima colata + linea + carro...' : 'Cilindri MONTATI su questa posizione...') :
                        isRientrato ? 'Cilindri IN RIPARAZIONE...' :
                        'N° cilindro...'
                      }
                      required
                      error={errors.ncilindro}
                      disabled={lockNcilindro}
                      icon="Hash"
                      sublabel={
                        isRientrato ? `(${opts.ncilindro.length} in riparazione)` :
                        isSmontato && !lockNcilindro ? `(${opts.ncilindro.length} trovati)` : ''
                      }
                    />
                  </div>

                  {cilindrInfo && (
                    <CilindroBadge
                      ncilindro={cilindrInfo.ncilindro}
                      stato={cilindrInfo.stato}
                      lastLinea={cilindrInfo.linea}
                      lastColata={cilindrInfo.colata}
                    />
                  )}
                </>
              )}

              {/* Posizione libera per ALTRO */}
              {isAltro && (
                <>
                  <SectionTitle label="Step 2 — Posizione (opzionale)" icon="MapPin" />
                  <ComboField label="Colata"        name="colata"       value={formData.colata}       onChange={handleChange} options={opts.colata}       placeholder="Opzionale..." icon="Layers"    />
                  <ComboField label="Linea"         name="linea"        value={formData.linea}        onChange={handleChange} options={opts.linea}        placeholder="Opzionale..." icon="GitBranch" />
                  <ComboField label="Carro/Paniera" name="carropaniera" value={formData.carropaniera} onChange={handleChange} options={opts.carropaniera} placeholder="Opzionale..." icon="Truck"     />
                </>
              )}

              {/* ── Step finale: Dettagli ── */}
              {formData.tipointervento && (
                <>
                  <SectionTitle
                    label={`Step ${isSmontato ? '4' : isMontato ? '4' : '3'} — Dettagli`}
                    icon="FileText"
                  />

                  <DatetimeField
                    label="Data Rientro (opzionale)"
                    name="datarientro"
                    value={formData.datarientro}
                    onChange={handleChange}
                    type="date"
                  />

                  {isAltro && (
                    <ComboField
                      label="Stato (opzionale)"
                      name="stato"
                      value={formData.stato}
                      onChange={handleChange}
                      options={opts.stato.length ? opts.stato : ['PRONTO', 'IN RIPARAZIONE']}
                      placeholder="Stato risultante..."
                      icon="Activity"
                    />
                  )}

                  <div className="col-span-full flex flex-col gap-1.5">
                    <div className="flex justify-between items-center px-1">
                      <label className={`text-[10px] font-black uppercase tracking-[.15em] select-none ${errors.note ? 'text-red-500' : 'text-muted-foreground/60'}`}>
                        Note / Motivo
                        {noteObbligatoria && <span className="text-primary ml-0.5">•</span>}
                        {!noteObbligatoria && <span className="ml-1 text-muted-foreground/30 text-[9px] normal-case font-bold tracking-normal">(opzionale)</span>}
                      </label>
                      {errors.note && (
                        <span className="text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1">
                          <Icon name="AlertCircle" size={9} />{errors.note}
                        </span>
                      )}
                    </div>
                    <textarea
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      placeholder={
                        isSmontato  ? 'Motivo smontaggio (obbligatorio)...' :
                        isMontato   ? 'Motivo montaggio (obbligatorio)...' :
                        isRientrato ? 'Note rientro...' :
                        'Note aggiuntive...'
                      }
                      rows={3}
                      className={`w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all resize-none ${
                        errors.note
                          ? 'bg-red-500/5 border-2 border-red-500/40 focus:ring-2 focus:ring-red-500/10'
                          : 'bg-muted/30 border border-border/30 focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60'
                      }`}
                    />
                  </div>

                  {/* Riepilogo */}
                  {formData.ncilindro && (
                    <div className="col-span-full p-4 rounded-2xl bg-muted/20 border border-border/30 flex flex-col gap-2">
                      <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">Riepilogo intervento</span>
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                        {[
                          { k: 'Tipo',     v: formData.tipointervento },
                          { k: 'Cilindro', v: formData.ncilindro },
                          { k: 'Stato →',  v: isSmontato ? 'IN RIPARAZIONE' : isRientrato ? 'PRONTO' : isMontato ? 'NULL' : formData.stato || '—' },
                          formData.colata       && { k: 'Colata',   v: formData.colata },
                          formData.linea        && { k: 'Linea',    v: formData.linea },
                          formData.carropaniera && { k: 'Carro',    v: formData.carropaniera },
                          currentUser && { k: 'Operatore', v: `${currentUser.nome} ${currentUser.cognome}`.trim() },
                        ].filter(Boolean).map(({ k, v }) => (
                          <span key={k} className="text-[11px]">
                            <span className="text-muted-foreground/40 font-bold uppercase text-[9px] tracking-widest">{k} </span>
                            <span className="font-black text-foreground/70">{v}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

            </div>
          </form>

          {/* ── FOOTER ── */}
          <div className="shrink-0 px-5 md:px-10 py-5 md:py-6 bg-muted/30 border-t border-border/40 flex flex-col sm:flex-row gap-3 relative z-10">
            <div className="hidden sm:flex items-center gap-2 text-muted-foreground/30 mr-auto">
              <Icon name="Info" size={12} />
              <span className="text-[9px] font-black uppercase tracking-[.2em]">
                {Object.values(formData).filter(v => v !== '' && v != null).length} campi compilati
              </span>
            </div>

            <button type="button" onClick={handleClose}
              className="flex-1 sm:flex-none sm:w-36 py-3 md:py-3.5 rounded-2xl bg-muted hover:bg-red-500/10 hover:text-red-500 border border-border/40 hover:border-red-500/20 text-foreground text-[11px] font-black uppercase tracking-[.2em] transition-all">
              Annulla
            </button>

            <button type="submit" onClick={handleSubmit} disabled={isLoading}
              className="flex-1 sm:flex-[2] py-3 md:py-3.5 rounded-2xl bg-primary text-primary-foreground text-[11px] font-black uppercase tracking-[.2em] shadow-lg shadow-primary/20 hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {isLoading ? (
                <>
                  <svg className="animate-spin" width="15" height="15" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity=".25" strokeWidth="2.5"/>
                    <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  Salvataggio...
                </>
              ) : (
                <>
                  <Icon name="Save" size={15} />
                  Salva Intervento
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </>
  );
};

export default NewInsertCilindri;